/**
 * Configuration options for currency formatting
 */
type GetFormattedCurrencyOptions = {
  /** Displays decimal places (2 digits) if amount has decimals, otherwise displays no decimals */
  shouldUseDecimals?: boolean;
  /** Custom currency unit to replace the default "kr" */
  unit?: string;
  /** Whether to always show decimal places (2 digits), even if amount is without decimals */
  shouldAlwaysShowDecimals?: boolean;
};

/**
 * Replaces the default currency unit ("kr" or "SEK") in the formatted string with a custom unit.
 *
 * @param formattedAmount - The currency string with default symbol (kr/SEK)
 * @param unit - The replacement unit to use
 * @returns The formatted string with the new unit
 */
function replaceUnit(formattedAmount: string, unit: string): string {
  return formattedAmount.replace(/kr|SEK/, unit);
}

function formatCurrencyWithoutDecimals(amount: number): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    roundingMode: 'trunc',
  }).format(amount);
}

function formatCurrencyWithDecimalsIfNeeded(amount: number): string {
  const hasDecimals = amount % 1 !== 0;

  if (hasDecimals) {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  return formatCurrencyWithoutDecimals(amount);
}

function formatCurrencyWithDecimalsAlways(amount: number): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a number as Swedish currency (SEK) with flexible display options.
 * Uses the Swedish locale format with proper thousand separators and decimal handling.
 *
 * @param amount - The monetary amount to format (string or number, defaults to 0)
 * @param options - Optional formatting configuration
 * @param options.shouldUseDecimals - Whether to show decimal places (2 digits if true, 0 if false)
 * @param options.unit - Custom currency unit to replace "kr" (e.g., "KR", "SEK")
 * @param options.shouldAlwaysShowDecimals - Whether to always show decimal places (2 digits), even if amount is without decimals
 * @returns The formatted currency string in Swedish format
 * @example
 * ```typescript
 * // Basic usage - shows whole numbers
 * getFormattedCurrency({ amount: 100 })
 * // → "100 kr"
 *
 * // With decimals - forces 2 decimal places
 * getFormattedCurrency({ amount: 100.5, options: { shouldUseDecimals: true } })
 * // → "100,50 kr"
 *
 * // Custom unit replacement
 * getFormattedCurrency({ amount: 100, options: { unit: "KR" } })
 * // → "100 KR"
 *
 * // String amounts are parsed automatically
 * getFormattedCurrency({ amount: "100.75", options: { shouldUseDecimals: true } })
 * // → "100,75 kr"
 *
 * // Large numbers with thousand separators
 * getFormattedCurrency({ amount: 1234567 })
 * // → "1 234 567 kr"
 *
 * // Handling undefined/null amounts
 * getFormattedCurrency({ amount: undefined })
 * // → "0 kr"
 * ```
 */
export function getFormattedCurrency(amount: string | number, options?: GetFormattedCurrencyOptions): string {
  const amountToUse = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
  const shouldUseDecimals = options?.shouldUseDecimals ?? false;
  const shouldAlwaysShowDecimals = options?.shouldAlwaysShowDecimals ?? false;
  const unit = options?.unit;

  let formattedAmount: string;

  if (shouldAlwaysShowDecimals) {
    formattedAmount = formatCurrencyWithDecimalsAlways(amountToUse);
  } else if (shouldUseDecimals) {
    formattedAmount = formatCurrencyWithDecimalsIfNeeded(amountToUse);
  } else {
    formattedAmount = formatCurrencyWithoutDecimals(amountToUse);
  }

  return unit === undefined ? formattedAmount : replaceUnit(formattedAmount, unit);
}
